import React, { useEffect, useRef, useState } from 'react';
import { FormDataConsumer } from 'react-admin';
import { TextField } from '@material-ui/core';
import { GOOGLE_MAPS_API_KEY, GOOGLE_PLACES_AUTOCOMPLETE_URL, GOOGLE_PLACES_DETAILS_URL } from '../config/googleApiConfig';

const API_KEY = GOOGLE_MAPS_API_KEY;

const AddressAutoComplete = ({ source, label, validate, style }) => {
    const [inputValue, setInputValue] = useState('');
    const [predictions, setPredictions] = useState([]);
    const [showPredictions, setShowPredictions] = useState(false);
    const [selectedPlace, setSelectedPlace] = useState(null);
    const searchInputRef = useRef(null);
    const predictionsRef = useRef(null);

    useEffect(() => {
        // 点击外部关闭预测下拉框
        const handleClickOutside = (event) => {
            if (
                searchInputRef.current && !searchInputRef.current.contains(event.target) &&
                predictionsRef.current && !predictionsRef.current.contains(event.target)
            ) {
                setShowPredictions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [API_KEY]);

    // 处理输入变化
    const handleInputChange = async (event, formData, dispatch) => {
        const value = event.target.value;
        setInputValue(value);

        // 当用户开始输入时，调用 Google Places API 获取预测
        if (value.length > 2) {
            try {
                const response = await fetch(GOOGLE_PLACES_AUTOCOMPLETE_URL, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY
                    },
                    body: JSON.stringify({
                        input: value,
                        includedPrimaryTypes: ['street_address', 'premise', 'subpremise']
                    })
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.suggestions && data.suggestions.length > 0) {
                        setPredictions(data.suggestions);
                        setShowPredictions(true);
                    } else {
                        setPredictions([]);
                        setShowPredictions(false);
                    }
                } else {
                    console.error('Autocomplete API error:', response.status);
                    setPredictions([]);
                    setShowPredictions(false);
                }
            } catch (error) {
                console.error('Error fetching autocomplete predictions:', error);
                setPredictions([]);
                setShowPredictions(false);
            }
        } else {
            setPredictions([]);
            setShowPredictions(false);
        }

        // 更新表单值
        dispatch({
            type: 'redux-form/CHANGE',
            meta: {
                form: 'record-form',
                field: source
            },
            payload: value
        });
    };

    // 处理选择预测项
    const handleSelectPrediction = async (suggestion, formData, dispatch) => {
        const placeId = suggestion.placePrediction && suggestion.placePrediction.placeId;
        const description = (suggestion.placePrediction && suggestion.placePrediction.text && suggestion.placePrediction.text.text) || '';
        
        setSelectedPlace(suggestion);
        setInputValue(description);
        setShowPredictions(false);

        // 更新表单值
        dispatch({
            type: 'redux-form/CHANGE',
            meta: {
                form: 'record-form',
                field: source
            },
            payload: description
        });

        // 获取完整的地址详情
        if (placeId) {
            try {
                const response = await fetch(
                    `${GOOGLE_PLACES_DETAILS_URL}/${placeId}`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
                            'X-Goog-FieldMask': 'addressComponents,formattedAddress'
                        }
                    }
                );

                if (response.ok) {
                    const place = await response.json();
                    
                    // 解析地址组件并填充其他表单字段
                    const addressComponents = {};
                    if (place.addressComponents) {
                        place.addressComponents.forEach(component => {
                            component.types.forEach(type => {
                                addressComponents[type] = component.longText || component.shortText;
                            });
                        });
                    }

                    // 填充州/省
                    if (addressComponents['administrative_area_level_1'] && formData && formData.address) {
                        dispatch({
                            type: 'redux-form/CHANGE',
                            meta: {
                                form: 'record-form',
                                field: 'address.state'
                            },
                            payload: addressComponents['administrative_area_level_1']
                        });
                    }

                    // 填充城市
                    if (addressComponents['locality'] && formData && formData.address) {
                        dispatch({
                            type: 'redux-form/CHANGE',
                            meta: {
                                form: 'record-form',
                                field: 'address.city'
                            },
                            payload: addressComponents['locality']
                        });
                    }

                    // 填充邮政编码
                    if (addressComponents['postal_code'] && formData && formData.address) {
                        dispatch({
                            type: 'redux-form/CHANGE',
                            meta: {
                                form: 'record-form',
                                field: 'address.postalCode'
                            },
                            payload: addressComponents['postal_code']
                        });
                    }
                } else {
                    console.error('Place Details API error:', response.status);
                }
            } catch (error) {
                console.error('Error fetching place details:', error);
            }
        }
    };

    return (
        <FormDataConsumer>
            {({ formData, dispatch }) => {
                // 获取当前地址值作为初始值
                const initialValue = formData && formData.address && formData.address.address1 
                    ? formData.address.address1 
                    : '';

                // 只有在初始加载时设置 inputValue
                useEffect(() => {
                    if (initialValue && !selectedPlace) {
                        setInputValue(initialValue);
                    }
                }, [initialValue, selectedPlace]);

                return (
                    <div style={{ position: 'relative', ...style }}>
                        <TextField
                            inputRef={searchInputRef}
                            label={label}
                            value={inputValue}
                            onChange={(e) => handleInputChange(e, formData, dispatch)}
                            variant="outlined"
                            fullWidth
                            error={false}
                            helperText={''}
                            placeholder="Start typing your address..."
                        />
                        
                        {showPredictions && predictions.length > 0 && (
                            <div
                                ref={predictionsRef}
                                style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    backgroundColor: 'white',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    zIndex: 1000,
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                            >
                                {predictions.map((suggestion) => {
                                    const mainText = (suggestion.placePrediction && suggestion.placePrediction.structuredFormat && suggestion.placePrediction.structuredFormat.mainText && suggestion.placePrediction.structuredFormat.mainText.text) || '';
                                    const secondaryText = (suggestion.placePrediction && suggestion.placePrediction.structuredFormat && suggestion.placePrediction.structuredFormat.secondaryText && suggestion.placePrediction.structuredFormat.secondaryText.text) || '';
                                    const placeId = (suggestion.placePrediction && suggestion.placePrediction.placeId) || '';
                                    
                                    return (
                                        <div
                                            key={placeId}
                                            onClick={() => handleSelectPrediction(suggestion, formData, dispatch)}
                                            style={{
                                                padding: '10px 15px',
                                                cursor: 'pointer',
                                                ':hover': {
                                                    backgroundColor: '#f5f5f5'
                                                }
                                            }}
                                        >
                                            <div style={{ fontWeight: 'bold' }}>{mainText}</div>
                                            <div style={{ color: '#666', fontSize: '0.9em' }}>
                                                {secondaryText}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            }}
        </FormDataConsumer>
    );
};

export default AddressAutoComplete;